// src/components/salesRepDashboard/SalesRepProviderPatients.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  IoArrowBack,
  IoCalendarOutline,
  IoMedkitOutline,
  IoChevronForward,
  IoPersonOutline,
  IoAlertCircle,
  IoSearchOutline,
} from "react-icons/io5";
import { Users, Loader2 } from "lucide-react";

/**
 * ProMed Health Plus — Sales Rep Provider Patients (Teal/Green Theme)
 * - Matches your navbar theme (teal/green)
 * - Clean header, search, stats, and professional list cards
 * - Dark mode safe and consistent surfaces
 */

const SalesRepProviderPatients = () => {
  const { providerId } = useParams();
  const navigate = useNavigate();

  const [patients, setPatients] = useState([]);
  const [providerInfo, setProviderInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  // UX helpers
  const [searchText, setSearchText] = useState("");

  const fetchPatients = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);

      const response = await fetch(
        `https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/sales-rep/providers/${providerId}/patients/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch patients");

      const data = await response.json();
      const list = data.data || [];
      setPatients(list);

      // If your API later returns provider metadata, plug it here
      // For now, keep it safe and professional
      setProviderInfo({ name: "Provider", id: providerId });
    } catch (err) {
      console.error("Error:", err);
      setErrorMessage(err?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId]);

  const pageBg =
    "min-h-screen " +
    "bg-gradient-to-br from-white via-teal-50/40 to-emerald-50/30 " +
    "dark:from-slate-950 dark:via-slate-950 dark:to-slate-900";

  const headerBar =
    "border-b border-teal-100/70 bg-white/80 backdrop-blur " +
    "dark:border-slate-800 dark:bg-slate-950/70";

  const surface =
    "border border-slate-200 bg-white shadow-sm " +
    "dark:border-slate-800 dark:bg-slate-900/60 dark:shadow-none";

  const surfaceSoft =
    "border border-slate-200 bg-slate-50 " +
    "dark:border-slate-800 dark:bg-slate-900/40";

  const filteredPatients = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return patients;

    return patients.filter((p) => {
      const fullName = String(p.full_name || "").toLowerCase();
      const mrn = String(p.medical_record_number || "").toLowerCase();
      const insurance = String(p.primary_insurance || "").toLowerCase();
      const wound = String(p.wound_type || "").toLowerCase();
      return (
        fullName.includes(q) ||
        mrn.includes(q) ||
        insurance.includes(q) ||
        wound.includes(q)
      );
    });
  }, [patients, searchText]);

  const formatDob = (dob) => dob || "N/A";

  const initials = (patient) => {
    const a = (patient?.first_name || patient?.full_name || "P").trim();
    const b = (patient?.last_name || "").trim();
    const first = a ? a[0] : "P";
    const second = b ? b[0] : (a.split(" ")[1]?.[0] || "");
    return `${first}${second}`.toUpperCase();
  };

  return (
    <div className={pageBg}>
      {/* Top Header */}
      <div className={headerBar}>
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-12">
          <button
            onClick={() => navigate("/dashboard")}
            className="group inline-flex items-center gap-2 text-sm font-semibold text-slate-600 transition hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-100"
          >
            <IoArrowBack className="text-lg transition group-hover:-translate-x-0.5" />
            Back to Dashboard
          </button>

          <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <div className="grid h-14 w-14 place-items-center rounded-2xl bg-teal-50 ring-1 ring-teal-100 dark:bg-teal-500/10 dark:ring-teal-500/20">
                <Users className="text-teal-700 dark:text-teal-300" size={28} />
              </div>
              <div>
                <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100 sm:text-3xl">
                  Provider Patients
                </h1>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  {providerInfo?.name ? (
                    <>
                      {providerInfo.name} •{" "}
                      <span className="font-semibold">
                        {patients.length} patient{patients.length !== 1 ? "s" : ""}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="font-semibold">
                        {patients.length} patient{patients.length !== 1 ? "s" : ""}
                      </span>
                    </>
                  )}
                </p>
              </div>
            </div>

            {/* Search */}
            <div className="w-full sm:w-[360px]">
              <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm dark:border-slate-800 dark:bg-slate-950">
                <IoSearchOutline className="text-lg text-slate-400" />
                <input
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search by name…"
                  className="w-full bg-transparent text-sm font-semibold text-slate-900 placeholder:text-slate-400 focus:outline-none dark:text-slate-100"
                />
              </div>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Showing <span className="font-semibold">{filteredPatients.length}</span> of{" "}
                <span className="font-semibold">{patients.length}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-12">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-12 w-12 animate-spin text-teal-600" />
            <p className="mt-4 text-sm font-semibold text-slate-600 dark:text-slate-400">
              Loading patients…
            </p>
          </div>
        ) : errorMessage ? (
          <div className="rounded-2xl border border-rose-200 bg-white p-6 shadow-sm dark:border-rose-500/30 dark:bg-slate-900/70">
            <div className="flex items-start gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-xl bg-rose-50 ring-1 ring-rose-200 dark:bg-rose-500/10 dark:ring-rose-500/30">
                <IoAlertCircle className="text-xl text-rose-600 dark:text-rose-300" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-extrabold text-slate-900 dark:text-slate-100">
                  Unable to load patients
                </p>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                  {errorMessage}
                </p>
              </div>
            </div>

            <div className="mt-5 flex gap-3">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:shadow-md dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200"
              >
                Back
              </button>
              <button
                onClick={fetchPatients}
                className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 px-4 py-2.5 text-sm font-extrabold text-white shadow-sm transition hover:from-teal-700 hover:to-emerald-700 hover:shadow-md"
              >
                Retry
              </button>
            </div>
          </div>
        ) : patients.length === 0 ? (
          <div className={`rounded-2xl p-12 text-center ${surface}`}>
            <Users className="mx-auto mb-4 h-14 w-14 text-slate-400" />
            <p className="text-lg font-extrabold text-slate-900 dark:text-slate-100">
              No patients yet
            </p>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Patients will appear here once added.
            </p>
          </div>
        ) : filteredPatients.length === 0 ? (
          <div className={`rounded-2xl p-10 text-center ${surface}`}>
            <IoPersonOutline className="mx-auto mb-3 text-4xl text-slate-400" />
            <p className="text-lg font-extrabold text-slate-900 dark:text-slate-100">
              No matches
            </p>
            <button
              onClick={() => setSearchText("")}
              className="mt-5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:shadow-md dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200"
            >
              Clear search
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredPatients.map((patient) => (
              <button
                key={patient.id}
                onClick={() => navigate(`/sales-rep/patients/${patient.id}`)}
                className={`group w-full rounded-2xl p-5 text-left transition-all hover:shadow-md ${surfaceSoft} hover:border-teal-300 dark:hover:border-teal-700`}
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  {/* Left */}
                  <div className="flex items-center gap-4">
                    <div className="grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-teal-600 to-emerald-600 text-white shadow-sm">
                      <span className="text-sm font-extrabold tracking-wide">
                        {initials(patient)}
                      </span>
                    </div>

                    <div className="min-w-0">
                      <p className="text-base font-extrabold text-slate-900 dark:text-slate-100">
                        {patient.full_name}
                      </p>

                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600 dark:text-slate-400">
                        <span className="inline-flex items-center gap-2">
                          <IoCalendarOutline className="text-slate-400" />
                          <span className="font-semibold">{formatDob(patient.date_of_birth)}</span>
                        </span>

                        {/* <span className="inline-flex items-center gap-2">
                          <IoMedkitOutline className="text-slate-400" />
                          <span className="font-semibold">
                            MRN: {patient.medical_record_number || "N/A"}
                          </span>
                        </span> */}
                      </div>
                    </div>
                  </div>

                  {/* Right */}
                  <div className="flex items-center justify-between gap-4 sm:justify-end">
                    {/* <div className="grid grid-cols-2 gap-3 sm:grid-cols-2">
                      <MiniMetric label="Insurance" value={patient.primary_insurance || "N/A"} />
                      <MiniMetric label="Wound" value={patient.wound_type || "N/A"} />
                    </div> */}

                    <IoChevronForward className="text-2xl text-slate-400 transition-all group-hover:translate-x-0.5 group-hover:text-teal-600 dark:group-hover:text-teal-300" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const MiniMetric = ({ label, value }) => {
  return (
    <div className="min-w-[140px] rounded-xl border border-slate-200 bg-white/60 px-3 py-2 dark:border-slate-800 dark:bg-slate-950/40">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p className="mt-0.5 truncate text-sm font-extrabold text-slate-900 dark:text-slate-100">
        {value}
      </p>
    </div>
  );
};

export default SalesRepProviderPatients;
